import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AibaLogo } from "./Logo";
import { scrollToId } from "../../hooks/useLenis";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { NotificationBell } from "./NotificationBell";
import { Menu, X, LogIn, Coins, LogOut, Globe, User, ChevronDown } from "lucide-react";

const LINK_IDS = [
  { key: "home", id: "hero" },
  { key: "academy", id: "academy" },
  { key: "tools", id: "cockpit" },
  { key: "store", id: "products" },
  { key: "community", id: "community" },
  { key: "pricing", id: "pricing" },
  { key: "contact", id: "contact" },
];

const LangSwitch = ({ className = "" }) => {
  const { lang, toggle } = useLang();
  return (
    <button
      data-testid="lang-switch"
      onClick={toggle}
      title={lang === "fr" ? "Switch to English" : "Passer en français"}
      className={`flex items-center gap-1.5 rounded-full border border-blue-500/25 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-blue-100 transition-all duration-300 hover:border-blue-400/50 hover:bg-blue-500/10 ${className}`}
    >
      <Globe size={13} className="text-blue-300" />
      {lang === "fr" ? "FR" : "EN"}
    </button>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, setAuthOpen, logout } = useAuth();
  const { t } = useLang();
  const LINKS = LINK_IDS.map((l) => ({ ...l, label: t(`nav.${l.key}`) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`safe-top fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled
          ? "bg-[#0F172A]/70 backdrop-blur-xl border-b border-blue-500/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="safe-x mx-auto flex h-20 max-w-[1500px] items-center justify-between px-6 lg:px-10">
        <button data-testid="nav-logo" onClick={() => go("hero")} className="group flex items-center gap-3">
          <AibaLogo size={30} className="transition-transform duration-500 group-hover:rotate-[10deg]" />
          <div className="text-left leading-none">
            <span className="font-display text-lg font-800 tracking-tighter text-white">AIBA</span>
            <span className="ml-2 hidden font-mono text-[10px] uppercase tracking-[0.3em] text-blue-300/70 sm:inline">
              Academy
            </span>
          </div>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l, i) => (
            <button
              key={`${l.label}-${i}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.id)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-[#CBD5E1] transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-blue-500 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-link-films"
            onClick={() => navigate("/films")}
            className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-[#CBD5E1] transition-colors duration-300 hover:text-white"
          >
            Films
            <span className="absolute -bottom-2 left-0 h-px w-0 bg-blue-500 transition-all duration-300 group-hover:w-full" />
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch className="hidden sm:flex" />
          {user ? (
            <>
              <NotificationBell />
              <button
                data-testid="nav-credits"
                onClick={() => scrollToId("credits")}
                className="hidden items-center gap-2 rounded-full border border-blue-400/40 bg-blue-500/10 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-blue-100 transition-all duration-300 hover:bg-blue-500/20 sm:flex"
              >
                <Coins size={14} className="text-blue-300" />
                {user.credits} {t("nav.credits")}
              </button>
              <div className="relative" ref={menuRef}>
                <button
                  data-testid="nav-account-toggle"
                  onClick={() => setMenuOpen((o) => !o)}
                  title={user.email}
                  className="flex items-center gap-2 rounded-full border border-blue-500/20 py-1.5 pl-1.5 pr-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[#CBD5E1] transition-all duration-300 hover:text-white"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-blue-400/40 object-cover" />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-blue-400/40 bg-blue-500/15"><User size={13} className="text-blue-200" /></span>
                  )}
                  <span className="hidden max-w-[90px] truncate sm:inline">{user.name || user.email}</span>
                  <ChevronDown size={13} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      data-testid="nav-account-menu"
                      className="absolute right-0 mt-2 w-56 overflow-hidden rounded-2xl border border-blue-500/20 bg-[#0B1220]/95 p-2 backdrop-blur-2xl shadow-2xl"
                    >
                      <div className="border-b border-blue-500/10 px-3 py-2.5">
                        <p className="truncate font-body text-sm text-white">{user.name || user.email}</p>
                        <p className="truncate font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{user.email}</p>
                      </div>
                      <button data-testid="nav-menu-account" onClick={() => { setMenuOpen(false); navigate("/compte"); }}
                        className="mt-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.15em] text-[#CBD5E1] hover:bg-blue-500/10 hover:text-white">
                        <User size={14} className="text-blue-300" /> Mon compte
                      </button>
                      <button data-testid="nav-menu-credits" onClick={() => { setMenuOpen(false); scrollToId("credits"); }}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.15em] text-[#CBD5E1] hover:bg-blue-500/10 hover:text-white">
                        <Coins size={14} className="text-blue-300" /> {user.credits} {t("nav.credits")}
                      </button>
                      <button data-testid="nav-menu-logout" onClick={() => { setMenuOpen(false); logout(); }}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.15em] text-red-300/80 hover:bg-red-500/10 hover:text-red-200">
                        <LogOut size={14} /> Déconnexion
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <button
              data-testid="nav-login"
              onClick={() => setAuthOpen(true)}
              className="hidden items-center gap-2 rounded-full border border-blue-400/40 bg-blue-500/10 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-100 transition-all duration-300 hover:bg-blue-600 hover:text-white sm:flex"
            >
              <LogIn size={14} /> {t("nav.login")}
            </button>
          )}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-blue-500/20 text-blue-200 lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-blue-500/10 bg-[#0F172A]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l, i) => (
                <button
                  key={`m-${l.label}-${i}`}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-[#CBD5E1] hover:text-white"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-link-films"
                onClick={() => { setOpen(false); navigate("/films"); }}
                className="py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-[#CBD5E1] hover:text-white"
              >
                Films
              </button>
              <button
                onClick={() => { setOpen(false); user ? navigate("/compte") : setAuthOpen(true); }}
                data-testid="nav-mobile-login"
                className="mt-2 flex items-center gap-2 py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-blue-300"
              >
                {user ? <User size={15} /> : <LogIn size={15} />} {user ? "Mon compte" : t("nav.login")}
              </button>
              {user && (
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  data-testid="nav-mobile-logout"
                  className="flex items-center gap-2 py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-red-300/80"
                >
                  <LogOut size={15} /> Déconnexion
                </button>
              )}
              <LangSwitch className="mt-2 w-fit" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
